import { useMemo } from 'react';
import { formatTimezoneWithOffset, getAllUniqueTimezones } from '../../utils/date';

type Props = {
  timezone: string;
  initialTZ: string;
  hourMode: '12h' | '24h' | string;
  onTimezoneChange: (tz: string) => void;
  onResetTimezone: () => void;
  onHourModeChange: (mode: '12h' | '24h' | string) => void;
};

const ScheduleTimezoneControls = (props: Props) => {
  const {
    timezone,
    initialTZ,
    hourMode,
    onTimezoneChange,
    onResetTimezone,
    onHourModeChange,
  } = props;

  const timezoneOptions = useMemo(() => {
    return getAllUniqueTimezones().map((tz) => ({
      value: tz,
      label: formatTimezoneWithOffset(tz, 'start'),
    }));
  }, []);

  return (
    <div className="flex flex-col md:flex-row items-center gap-6 text-sm text-gray-700">
      <div className="flex flex-col sm:flex-row items-center gap-2 sm:w-auto">
        <span className="text-base font-semibold text-nowrap">Shown in</span>
        <select
          className="border-b border-gray-400 bg-transparent px-2 py-1 w-full text-base font-semibold focus:outline-none"
          value={timezone}
          onChange={(e) => onTimezoneChange(e.target.value)}>
          {timezoneOptions.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>

        {/* Reset to the default timezone */}
        {timezone !== initialTZ && (
          <button
            title="Reset to your timezone"
            className="text-base font-semibold text-emerald-700 hover:text-emerald-900"
            onClick={onResetTimezone}>
            ↻
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <select
          className="border-b border-gray-400 bg-transparent text-base font-semibold px-1 py-1 focus:outline-none"
          value={hourMode}
          onChange={(e) => onHourModeChange(e.target.value)}>
          <option value="12h">12h</option>
          <option value="24h">24h</option>
        </select>

        {/* Reset to the default hour mode */}
        {hourMode !== '12h' && (
          <button
            title="Reset to 12h mode"
            className="text-base font-semibold text-emerald-700 hover:text-emerald-900"
            onClick={() => onHourModeChange('12h')}>
            ↻
          </button>
        )}
      </div>
    </div>
  );
};

export default ScheduleTimezoneControls;
